import { Plus, ArrowLeft, X, Menu } from "lucide-react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import "../../assets/styles/Admindashboard.css";
import { useNotificationSignalR } from "../../services/useNotificationSignalR.js";
import { useAdminState } from "../../components/adminDashboard/hooks/useAdminState.js";
import { DEFAULT_NEW_WORKSPACE } from "../../components/adminDashboard/utils/constants.js";

import Sidebar from "../../components/common/Sidebar.jsx";
import { ADMIN_NAV_ITEMS } from "../../config/adminNavConfig.js";
import { Toast } from "../../components/adminDashboard/index.jsx";
import { transformMenuData } from "../../utils/menuTransform.js";
import NotificationsPanel from "../../components/common/NotificationPanel.jsx";
import { authService } from "../../services/index.js";

function getPageTitle({
  isOnboarding,
  isUserOnboarding,
  isWorkspaceOnboarding,
  isWorkspaceMappingOnboarding,
  isRoleOnboarding,
  selectedCompany,
  selectedUserDetails,
  selectedRoleDetails,
  selectedWorkspaceMapping,
  activeTab,
  isChangePassword,
  isUserModelGroupMapping,
}) {
  if (isChangePassword) return "Change Password";
  if (isOnboarding) return "Company Onboarding";
  if (isUserOnboarding) return "User Onboarding";
  if (isWorkspaceOnboarding) return "Create Workspace";
  if (isWorkspaceMappingOnboarding)
    return selectedWorkspaceMapping
      ? "Edit Workspace Mapping"
      : "Create Workspace Mapping";
  if (isRoleOnboarding)
    return selectedRoleDetails ? "Edit Role" : "Create Role";
  if (isUserModelGroupMapping) return "User Model Group Mapping";
  if (selectedCompany) return selectedCompany.name;
  if (selectedUserDetails)
    return selectedUserDetails.firstName + " " + selectedUserDetails.lastName;

  const titles = {
    dashboard: "Overview",
    companies: "Companies",
    users: "User Management",
    workspaces: "Workspaces",
    workspaceMapping: "Workspace Mapping",
    roles: "Role Management",
    data: "Data Sources & Integrations",
    settings: "System Settings",
    usermodelgroupmapping: "User Model Group Mapping",
  };
  return titles[activeTab] || "";
}

function getPageSubtitle({
  isOnboarding,
  isUserOnboarding,
  isWorkspaceOnboarding,
  isWorkspaceMappingOnboarding,
  isRoleOnboarding,
  selectedCompany,
  selectedUserDetails,
  selectedRoleDetails,
  selectedWorkspaceMapping,
  activeTab,
  isChangePassword,
  isUserModelGroupMapping,
}) {
  if (isChangePassword) 
    return "Update your password to maintain account security. Choose a strong, unique password.";
  if (isOnboarding)
    return "Follow the steps to set up a new enterprise tenant.";
  if (isUserOnboarding)
    return "Provision a new user account and assign them to a company.";
  if (isWorkspaceOnboarding)
    return "Configure a tailored AI workspace with specific databases and documents.";
  if (isWorkspaceMappingOnboarding)
    return selectedWorkspaceMapping
      ? "Adjust mapped table, column, and PDF access for this user mapping."
      : "Assign workspace access and granular permissions to users.";
  if (isRoleOnboarding)
    return selectedRoleDetails
      ? "Update role name and default page assignment."
      : "Create a new platform role and set its default page.";
  if (isUserModelGroupMapping)
    return "Assign AI model groups to users for personalized experience and access control.";
  if (selectedCompany)
    return `Tenant ID: ${selectedCompany.companyID} • Industry: ${selectedCompany.companyName}`;
  if (selectedUserDetails)
    return `User Profile • ${selectedUserDetails.groupId == "1" ? "Admin" : "User"}`;
  if (activeTab === "dashboard")
    return "Global platform metrics, revenue, and system health overview.";
  if (activeTab === "users")
    return "Manage platform access, roles, and enterprise assignments across all tenants.";
  if (activeTab === "workspaces")
    return "Manage tailored AI workspaces, connecting specific documents and SQL schemas for teams.";
  if (activeTab === "workspaceMapping")
    return "Map workspaces to users within each company and keep access in sync.";
  if (activeTab === "roles")
    return "Manage platform roles and default landing pages.";
  if (activeTab === "usermodelgroupmapping")
    return "Assign AI model groups to users for personalized experience and access control.";
  return "Manage your enterprise AI ecosystem and global settings.";
}

export default function AdminLayout({ user, darkMode, setDarkMode }) {
  const location = useLocation();
  const [openNotif, setOpenNotif] = useState(false);

  // Must be called before useNotificationSignalR so selectedCompany is in scope
  const {
    activeTab,
    handleNavChange,
    showToast,
    triggerToast,
    apiMessage,
    setApiMessage,
    success,
    setSuccess,
    companiesData,
    searchQuery,
    setSearchQuery,
    isOnboarding,
    setIsOnboarding,
    selectedCompany,
    setSelectedCompany,
    onboardStep,
    setOnboardStep,
    handleFinishCompanyOnboarding,
    handleCancelCompanyOnboarding,
    usersData,
    userSearchQuery,
    setUserSearchQuery,
    isUserOnboarding,
    setIsUserOnboarding,
    selectedUserDetails,
    setSelectedUserDetails,
    userOnboardStep,
    setUserOnboardStep,
    handleFinishUserOnboarding,
    getUserList,
    workspacesData,
    workspaceSearchQuery,
    setWorkspaceSearchQuery,
    isWorkspaceOnboarding,
    setIsWorkspaceOnboarding,
    isWorkspaceMappingOnboarding,
    setIsWorkspaceMappingOnboarding,
    workspaceOnboardStep,
    setWorkspaceOnboardStep,
    newWorkspaceData,
    setNewWorkspaceData,
    handleFinishWorkspaceOnboarding,
    selectedWorkspaceDetails,
    setSelectedWorkspaceDetails,
    selectedDataCompanyId,
    setSelectedDataCompanyId,
    selectedWorkspaceMapping,
    setSelectedWorkspaceMapping,
    isRoleOnboarding,
    setIsRoleOnboarding,
    selectedRoleDetails,
    setSelectedRoleDetails,
    isAddingModel,
    setIsAddingModel,
    systemModels,
    newModelData,
    setNewModelData,
    handleAddModel,
    handleCancelAddModel,
    groups,
    fetchWorkWorkspaces,
    getCompanyList,
    fetchGroups,
  } = useAdminState();

  const suppressGenericToast = useRef(false);

  const handleShowToast = useCallback(({ message, type, suppressGeneric = true }) => {
    setApiMessage(message);
    setSuccess(type === "success");
    triggerToast(5000);
    getCompanyList(); // Refresh company list to reflect any status changes
    if (suppressGeneric) {
      suppressGenericToast.current = true;
      // Clear after a delay in case unreadCount doesn't change (effect won't run)
      setTimeout(() => {
        suppressGenericToast.current = false;
      }, 3000);
    }
  }, [setApiMessage, setSuccess, triggerToast, getCompanyList]);

   const { notifications, unreadCount, refetch } =
     useNotificationSignalR({ onShowToast: handleShowToast });

  const navItems = useMemo(() => {
    const menuData = localStorage.getItem("menuData");
    if (menuData) {
      try {
        return transformMenuData(JSON.parse(menuData));
      } catch (e) {
        console.error("Failed to parse menu data:", e);
      }
    }
    return ADMIN_NAV_ITEMS;
  }, []);

  // const initialLoadDone = useRef(false);
  // const lastUnreadCount = useRef(0);

  // // Show toast for new notifications (generic)
  // useEffect(() => {
  //   // Suppress generic toast if a specific toast was just shown (e.g., from SeedStatus)
  //   if (suppressGenericToast.current) {
  //     suppressGenericToast.current = false;
  //     return;
  //   }
  //   if (initialLoadDone.current && unreadCount > lastUnreadCount.current) {
  //     const newCount = unreadCount - lastUnreadCount.current;
  //     triggerToast(5000);
  //     setApiMessage(`You have ${newCount} new notification${newCount > 1 ? 's' : ''}`);
  //     setSuccess(true);
  //   }
  //   if (unreadCount > 0) {
  //     initialLoadDone.current = true;
  //   }
  //   lastUnreadCount.current = unreadCount;
  // }, [unreadCount, setApiMessage, setSuccess, triggerToast]);

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [vw, setVw] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setVw(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  const isMobile = vw < 768;

  // ── Resizable sidebar ──────────────────────────────────────────
  const COLLAPSED_W = 64;
  const MIN_W = 180;
  const MAX_W = 400;
  const DEFAULT_W = 256;
  const [collapsed, setCollapsed] = useState(vw < 1100);
  const [sidebarW, setSidebarW] = useState(DEFAULT_W);
  const wrapperRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, startW: 0 });

  const prevVw = useRef(vw);
  useEffect(() => {
    if (prevVw.current >= 1100 && vw < 1100) setCollapsed(true);
    if (prevVw.current < 1100 && vw >= 1100) setCollapsed(false);
    prevVw.current = vw;
  }, [vw]);

  const effectiveW = collapsed ? COLLAPSED_W : sidebarW;

  const onDragStart = (e) => {
    if (collapsed) return;
    e.preventDefault();
    const el = wrapperRef.current;
    if (!el) return;
    const startX = e.clientX;
    const startW = el.getBoundingClientRect().width;
    dragRef.current = { active: true, startX, startW };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    el.style.transition = "none";
    const move = (ev) => {
      if (!dragRef.current.active) return;
      const newW = Math.max(MIN_W, Math.min(MAX_W, dragRef.current.startW + (ev.clientX - dragRef.current.startX)));
      el.style.width = newW + "px";
    };
    const up = () => {
      if (!dragRef.current.active) return;
      dragRef.current.active = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      el.style.transition = "";
      setSidebarW(Math.round(el.getBoundingClientRect().width));
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };
  // ──────────────────────────────────────────────────────────────────

  useEffect(() => { if (!isMobile) setMobileSidebarOpen(false); }, [isMobile]);
  useEffect(() => { setMobileSidebarOpen(false); }, [location.pathname]);
  useEffect(() => {
    const handler = () => setMobileSidebarOpen(false);
    document.addEventListener("sidebar-close", handler);
    return () => document.removeEventListener("sidebar-close", handler);
  }, []);

  const isChangePassword = location.pathname === '/admin/change-password';
  const isUserModelGroupMapping = location.pathname === '/admin/usermodelgroupmapping';

  // Sync activeTab with URL
  useEffect(() => {
    const path = location.pathname.split("/")[2] || "dashboard";
    const tabMap = {
      "": "dashboard",
      companies: "companies",
      users: "users",
      workspaces: "workspaces",
      "workspace-mapping": "workspaceMapping",
      roles: "roles",
      data: "data",
      settings: "settings",
      usermodelgroupmapping: "usermodelgroupmapping",
    };
    const newTab = tabMap[path] || "dashboard";
    if (newTab !== activeTab) {
      handleNavChange(newTab);
    }
  }, [location.pathname]);

  const noSubView =
    !isOnboarding &&
    !selectedCompany &&
    !isUserOnboarding &&
    !selectedUserDetails &&
    !isWorkspaceOnboarding &&
    !isWorkspaceMappingOnboarding &&
    !isRoleOnboarding;

  const routeState = {
    activeTab,
    isOnboarding,
    isUserOnboarding,
    isWorkspaceOnboarding,
    isWorkspaceMappingOnboarding,
    isRoleOnboarding,
    selectedCompany,
    selectedUserDetails,
    selectedRoleDetails,
    selectedWorkspaceMapping,
    isChangePassword,
    isUserModelGroupMapping: location.pathname === '/admin/user-model-group-mapping',
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      sessionStorage.clear();
      localStorage.removeItem("refreshToken"); 
      localStorage.removeItem("accessToken");
      localStorage.removeItem("userId");
      localStorage.removeItem("groupId");
      localStorage.removeItem("expiresAtUtc");
      localStorage.removeItem("menuData");
      localStorage.removeItem("user");
      localStorage.removeItem("gxp_frontend_api_logs");
      localStorage
      window.location.href = "/login";
    } catch (error) {
      console.log(error.message || "Logout failed. Please try again.");
    }
  };

  return (
    <div className="ad-root">
      {/* Desktop resizable sidebar wrapper */}
      {!isMobile && (
        <div
          ref={wrapperRef}
          className="relative flex-shrink-0 h-full transition-[width] duration-200"
          style={{ width: effectiveW }}
        >
          <Sidebar
            navItems={navItems}
            activeTab={activeTab}
            onNavChange={handleNavChange}
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            user={user}
            handleLogout={handleLogout}
            onNotificationClick={() => setOpenNotif(true)}
            unreadCount={unreadCount}
            isSidebarOpen={true}
            collapsed={collapsed}
            onCollapsedChange={(c) => {
              setCollapsed(c);
              if (!c) setSidebarW(DEFAULT_W);
            }}
          />
          {/* Drag handle — only when expanded */}
          {!collapsed && (
            <div
              onMouseDown={onDragStart}
              className="absolute right-0 top-0 bottom-0 w-2 z-30 cursor-col-resize group"
            >
              <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-0.5 bg-slate-200 dark:bg-slate-700 group-hover:w-1 group-hover:bg-[#1d57a5] transition-all duration-150" />
            </div>
          )}
        </div>
      )}
      {/* Mobile sidebar */}
      {isMobile && (
        <Sidebar
          navItems={navItems}
          activeTab={activeTab}
          onNavChange={handleNavChange}
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          user={user}
          handleLogout={handleLogout}
          onNotificationClick={() => setOpenNotif(true)}
          unreadCount={unreadCount}
          isSidebarOpen={mobileSidebarOpen}
          collapsed={false}
          onCollapsedChange={() => {}}
        />
      )}

      <NotificationsPanel
        isOpen={openNotif}
        onClose={() => setOpenNotif(false)}
        initialNotifications={notifications} // ✅
        onRefetch={refetch}
      />

      <main className="ad-main">
        {/* Mobile top bar — outside ad-content so it doesn't conflict with sticky header */}
        {isMobile && (
          <div className="flex items-center gap-3 px-4 py-3 bg-[#e8edf3] dark:bg-[#0f172a] border-b border-slate-200 dark:border-slate-700 flex-shrink-0 sticky top-0 z-30">
            <button
              className="p-2 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              onClick={() => setMobileSidebarOpen(true)}
            >
              <Menu size={20} />
            </button>
            <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Admin Console</span>
          </div>
        )}
        <div className="ad-content">
          <header className="ad-page-header">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="ad-page-title">{getPageTitle(routeState)}</h1>
                <p className="ad-page-subtitle">{getPageSubtitle(routeState)}</p>
              </div>
            </div>

            <div className="ad-header-actions">
              {noSubView && activeTab === "companies" && (
                <button
                  onClick={() => setIsOnboarding(true)}
                  className="ad-btn ad-btn-blue"
                >
                  <Plus size={18} /> New Company Onboard
                </button>
              )}
              {noSubView && activeTab === "users" && (
                <button
                  onClick={() => setIsUserOnboarding(true)}
                  className="ad-btn ad-btn-blue"
                >
                  <Plus size={18} /> New User
                </button>
              )}
              {noSubView && activeTab === "workspaces" && (
                <button
                  onClick={() => setIsWorkspaceOnboarding(true)}
                  className="ad-btn ad-btn-blue"
                >
                  <Plus size={18} /> New Workspace
                </button>
              )}
              {noSubView && activeTab === "workspaceMapping" && (
                <button
                  onClick={() => {
                    setSelectedWorkspaceMapping(null);
                    setIsWorkspaceMappingOnboarding(true);
                  }}
                  className="ad-btn ad-btn-blue"
                >
                  <Plus size={18} /> Create New Mapping
                </button>
              )}
              {noSubView && activeTab === "roles" && (
                <button
                  onClick={() => {
                    setSelectedRoleDetails(null);
                    setIsRoleOnboarding(true);
                  }}
                  className="ad-btn ad-btn-blue"
                >
                  <Plus size={18} /> Add Role
                </button>
              )}
              {selectedCompany && (
                <button
                  onClick={() => setSelectedCompany(null)}
                  className="ad-btn ad-btn-outline"
                >
                  <ArrowLeft size={18} /> Back to Directory
                </button>
              )}
              {selectedUserDetails && !isUserOnboarding && (
                <button
                  onClick={() => setSelectedUserDetails(null)}
                  className="ad-btn ad-btn-outline"
                >
                  <ArrowLeft size={18} /> Back to Users
                </button>
              )}
              {isOnboarding && (
                <button
                  onClick={() => {
                    setIsOnboarding(false);
                    setOnboardStep(1);
                  }}
                  className="ad-btn ad-btn-outline"
                >
                  <X size={18} /> Cancel Onboarding
                </button>
              )}
              {isUserOnboarding && (
                <button
                  onClick={() => {
                    setIsUserOnboarding(false);
                    setUserOnboardStep(1);
                  }}
                  className="ad-btn ad-btn-outline"
                >
                  <X size={18} /> Cancel Onboarding
                </button>
              )}
              {isWorkspaceOnboarding && (
                <button
                  onClick={() => {
                    setIsWorkspaceOnboarding(false);
                    setWorkspaceOnboardStep(1);
                    setNewWorkspaceData(DEFAULT_NEW_WORKSPACE);
                    setSelectedWorkspaceDetails(null);
                  }}
                  className="ad-btn ad-btn-outline"
                >
                  <X size={18} />{" "}
                  {selectedWorkspaceDetails ? "Cancel Edit" : "Cancel Creation"}
                </button>
              )}
              {isWorkspaceMappingOnboarding && (
                <button
                  onClick={() => {
                    setIsWorkspaceMappingOnboarding(false);
                    setSelectedWorkspaceMapping(null);
                  }}
                  className="ad-btn ad-btn-outline"
                >
                  <X size={18} />{" "}
                  {selectedWorkspaceMapping ? "Cancel Edit" : "Cancel Mapping"}
                </button>
              )}
              {isRoleOnboarding && (
                <button
                  onClick={() => {
                    setIsRoleOnboarding(false);
                    setSelectedRoleDetails(null);
                  }}
                  className="ad-btn ad-btn-outline"
                >
                  <X size={18} />{" "}
                  {selectedRoleDetails ? "Cancel Edit" : "Cancel Role Creation"}
                </button>
              )}
              {selectedWorkspaceDetails && !isWorkspaceOnboarding && (
                <button
                  onClick={() => setSelectedWorkspaceDetails(null)}
                  className="ad-btn ad-btn-outline"
                >
                  <ArrowLeft size={18} /> Back to Directory
                </button>
              )}
            </div>
          </header>

          <Outlet
            context={{
              companiesData,
              searchQuery,
              setSearchQuery,
              isOnboarding,
              setIsOnboarding,
              selectedCompany,
              setSelectedCompany,
              onboardStep,
              setOnboardStep,
              handleFinishCompanyOnboarding,
              usersData,
              userSearchQuery,
              setUserSearchQuery,
              isUserOnboarding,
              setIsUserOnboarding,
              selectedUserDetails,
              setSelectedUserDetails,
              userOnboardStep,
              setUserOnboardStep,
              handleFinishUserOnboarding,
              getUserList,
              groups,
              workspacesData,
              workspaceSearchQuery,
              setWorkspaceSearchQuery,
              isWorkspaceOnboarding,
              setIsWorkspaceOnboarding,
              workspaceOnboardStep,
              setWorkspaceOnboardStep,
              newWorkspaceData,
              setNewWorkspaceData,
              handleFinishWorkspaceOnboarding,
              selectedWorkspaceDetails,
              setSelectedWorkspaceDetails,
              selectedDataCompanyId,
              setSelectedDataCompanyId,
              triggerToast,
              isAddingModel,
              setIsAddingModel,
              systemModels,
              newModelData,
              setNewModelData,
              handleAddModel,
              handleCancelAddModel,
              fetchWorkWorkspaces,
              getCompanyList,
              fetchGroups,
              isWorkspaceMappingOnboarding,
              setIsWorkspaceMappingOnboarding,
              selectedWorkspaceMapping,
              setSelectedWorkspaceMapping,
              isRoleOnboarding,
              setIsRoleOnboarding,
              selectedRoleDetails,
              setSelectedRoleDetails,
            }}
          />
        </div>
      </main>

      <Toast visible={showToast} message={apiMessage} success={success} />
    </div>
  );
}
